import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Clock, TrendingUp, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { apiClient } from '../App';
import { useNavigate } from 'react-router-dom';

/**
 * SearchSuggestions Component
 * Provides autocomplete, search history, and popular searches
 */
const SearchSuggestions = ({
  query,
  onSelect,
  onClose,
  isOpen,
  inputRef,
  themeStyles = {},
  // T11: inlineMode renders as a block (no absolute positioning) for mobile overlays
  inlineMode = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [popularSearches, setPopularSearches] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Load recent and popular searches on mount
  useEffect(() => {
    loadRecentSearches();
    fetchPopularSearches();
  }, []);

  // Load recent searches from localStorage
  const loadRecentSearches = () => {
    try {
      const stored = localStorage.getItem('recentSearches');
      if (stored) {
        setRecentSearches(JSON.parse(stored).slice(0, 5));
      }
    } catch (e) {
      console.error('Failed to load recent searches:', e);
    }
  };

  // Save search to recent searches
  const saveRecentSearch = (searchTerm) => {
    try {
      const stored = localStorage.getItem('recentSearches');
      let searches = stored ? JSON.parse(stored) : [];
      
      // Remove if already exists
      searches = searches.filter(s => s.toLowerCase() !== searchTerm.toLowerCase());
      
      // Add to beginning
      searches.unshift(searchTerm);
      
      // Keep only last 10
      searches = searches.slice(0, 10);
      
      localStorage.setItem('recentSearches', JSON.stringify(searches));
      setRecentSearches(searches.slice(0, 5));
    } catch (e) {
      console.error('Failed to save recent search:', e);
    }
  };

  // Fetch popular searches
  const fetchPopularSearches = async () => {
    try {
      const res = await apiClient.get('/search/popular');
      setPopularSearches(res.data?.searches || []);
    } catch (error) {
      console.error('Failed to fetch popular searches:', error);
    }
  };

  // Fetch suggestions when query changes
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      setSelectedIndex(-1);
      return;
    }

    // Debounce API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/search/suggestions?q=${encodeURIComponent(query)}`);
        setSuggestions(res.data?.suggestions || []);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    const items = suggestions.length > 0 
      ? suggestions 
      : recentSearches.length > 0 
        ? recentSearches 
        : popularSearches;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < items.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && items[selectedIndex]) {
          handleSelect(items[selectedIndex]);
        } else if (query.trim()) {
          handleSelect(query);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [isOpen, suggestions, recentSearches, popularSearches, selectedIndex, query]);

  // Add keyboard listener
  useEffect(() => {
    const input = inputRef?.current;
    if (input) {
      input.addEventListener('keydown', handleKeyDown);
      return () => input.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, inputRef]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleSelect = (term) => {
    const searchTerm = typeof term === 'object' ? (term.term || term.title || '') : String(term || '');
    if (!searchTerm.trim()) return;
    saveRecentSearch(searchTerm);
    onSelect(searchTerm);
    onClose();
  };

  const handleRemoveRecent = (term, e) => {
    e.stopPropagation();
    try {
      const stored = localStorage.getItem('recentSearches');
      if (stored) {
        const searches = JSON.parse(stored).filter(s => s !== term);
        localStorage.setItem('recentSearches', JSON.stringify(searches));
        setRecentSearches(searches.slice(0, 5));
      }
    } catch (e) {
      console.error('Failed to remove recent search:', e);
    }
  };

  const clearAllRecent = () => {
    localStorage.removeItem('recentSearches');
    setRecentSearches([]);
  };

  if (!isOpen) return null;

  const isDark = themeStyles?.bg?.includes('dark') || themeStyles?.bg?.includes('#09090B');
  const bgColor = isDark ? 'bg-[#0C0C0E]' : 'bg-white';
  const textColor = isDark ? 'text-[#FAFAFA]' : 'text-[#09090B]';
  const mutedColor = isDark ? 'text-[#A1A1AA]' : 'text-[#71717A]';
  const borderColor = isDark ? 'border-[#27272A]' : 'border-[#E4E4E7]';
  const hoverBg = isDark ? 'hover:bg-[#18181B]' : 'hover:bg-[#F4F4F5]';

  return (
    <div
      ref={containerRef}
      className={
        inlineMode
          ? `${bgColor} w-full h-full`
          : `absolute top-full left-0 right-0 mt-2 ${bgColor} ${borderColor} border rounded-lg shadow-2xl z-[100] max-h-[400px] overflow-y-auto`
      }
    >
      {/* Loading State */}
      {loading && (
        <div className="p-4 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-[#FC0000]" />
        </div>
      )}

      {/* Suggestions from API */}
      {!loading && suggestions.length > 0 && (
        <div className="py-2">
          <div className={`px-4 py-2 text-xs font-semibold uppercase ${mutedColor}`}>
            Suggestions
          </div>
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSelect(suggestion.term)}
              className={`w-full px-4 py-2.5 text-left flex items-center gap-3 ${hoverBg} transition-colors ${
                selectedIndex === index ? 'bg-[#FC0000]/10' : ''
              }`}
            >
              <Search className={`h-4 w-4 ${mutedColor}`} />
              <span className={`${textColor} text-sm`}>
                {(() => {
                  const text = suggestion.term || suggestion.text || '';
                  const q = query || '';
                  if (!q || !text) return text;
                  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                  const regex = new RegExp(`(${escaped})`, 'gi');
                  const parts = text.split(regex);
                  return parts.map((part, i) =>
                    regex.test(part) ? <mark key={i} className="bg-yellow-200/60 rounded px-0.5">{part}</mark> : part
                  );
                })()}
              </span>
              {suggestion.count && (
                <span className={`ml-auto text-xs ${mutedColor}`}>
                  {suggestion.count} results
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Recent Searches */}
      {!loading && !query.trim() && recentSearches.length > 0 && (
        <div className="py-2 border-b border-gray-200 dark:border-gray-800">
          <div className={`px-4 py-2 text-xs font-semibold uppercase ${mutedColor} flex items-center justify-between`}>
            <span>Recent Searches</span>
            <button 
              onClick={clearAllRecent}
              className="text-[#FC0000] hover:underline text-xs normal-case"
            >
              Clear All
            </button>
          </div>
          {recentSearches.map((term, index) => (
            <button
              key={`recent-${index}`}
              onClick={() => handleSelect(term)}
              className={`w-full px-4 py-2.5 text-left flex items-center gap-3 ${hoverBg} transition-colors ${
                selectedIndex === index ? 'bg-[#FC0000]/10' : ''
              }`}
            >
              <Clock className={`h-4 w-4 ${mutedColor}`} />
              <span className={`${textColor} text-sm flex-1`}>{term}</span>
              <button
                onClick={(e) => handleRemoveRecent(term, e)}
                className={`p-1 rounded hover:bg-black/10 ${mutedColor}`}
              >
                <X className="h-3 w-3" />
              </button>
            </button>
          ))}
        </div>
      )}

      {/* Popular Searches */}
      {!loading && !query.trim() && popularSearches.length > 0 && (
        <div className="py-2">
          <div className={`px-4 py-2 text-xs font-semibold uppercase ${mutedColor}`}>
            Popular Searches
          </div>
          {popularSearches.map((term, index) => (
            <button
              key={`popular-${index}`}
              onClick={() => handleSelect(term)}
              className={`w-full px-4 py-2.5 text-left flex items-center gap-3 ${hoverBg} transition-colors ${
                selectedIndex === (suggestions.length + recentSearches.length + index) ? 'bg-[#FC0000]/10' : ''
              }`}
            >
              <TrendingUp className={`h-4 w-4 ${mutedColor}`} />
              <span className={`${textColor} text-sm`}>{term}</span>
            </button>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !query.trim() && recentSearches.length === 0 && popularSearches.length === 0 && (
        <div className={`p-4 text-center ${mutedColor} text-sm`}>
          Start typing to search for parts...
        </div>
      )}
    </div>
  );
};

export default SearchSuggestions;
